import { PropType, defineComponent, h, watch } from 'vue';
import { Modal, Width } from '../modal';
import { Option } from '@vaaas/fpts/option';
import { Button } from '../button';
import { isEnter } from '@frontend/keyboard';

const is_text_field = (x: unknown): x is HTMLInputElement => x instanceof HTMLInputElement;

const is_empty = (x: HTMLInputElement | HTMLTextAreaElement) => x.value.length > 0;

export const Dialog = defineComponent({
    props: {
        id: { required: false, type: String },

        title: { required: false, type: String, default: undefined },

        width: { required: false, type: String as PropType<Width>, default: 'normal' },

        backdrop: { required: false, type: Boolean, default: true },

        show: { required: true, type: Boolean },

        dismissible: { required: false, type: Boolean, default: true },

        draggable: { required: false, type: Boolean, default: true },

        unload: { required: false, type: Boolean, default: true },

        style: {
            required: false,
            type: Object as PropType<Record<string, string>>,
            default: () => ({}),
        },

        close_label: {
            required: false,
            type: null as any as PropType<Option<string>>,
            default: 'Close',
        },

        confirm_label: {
            required: false,
            type: null as any as PropType<string | null | false>,
            default: 'Submit',
        },

        confirm_enabled: {
            required: false,
            type: Boolean,
            default: true,
        },
    },

    emits: {
        'update:show': (x: boolean) => true,
        close: () => true,
        confirm: () => true,
    },

    setup(props, { slots, emit }) {
        function on_document_key(e: KeyboardEvent) {
            if (
                isEnter(e)
                && props.confirm_enabled
                && (
                    !is_text_field(e)
                    || (is_text_field(e) || !is_empty(e))
                )
            )
                if (props.confirm_label)
                    emit('confirm');
                else
                    emit('close');
        }

        watch(
            () => props.show,
            (value) => {
                if (value)
                    document.documentElement.addEventListener('keyup', on_document_key);
                else
                    document.documentElement.removeEventListener('keyup', on_document_key);
            },
        );

        return () => h(Modal, {
            id: props.id,
            title: props.title,
            width: props.width,
            backdrop: props.backdrop,
            show: props.show,
            dismissible: props.dismissible,
            draggable: props.draggable,
            unload: props.unload,
            style: props.style,
            'onUpdate:show': x => emit('update:show', x),
        }, {
            body: slots.default,
            footer: () => [
                props.close_label && h(Button, { onClick: () => emit('close') }, () => props.close_label),
                props.confirm_label && h(Button, {
                    disabled: !props.confirm_enabled,
                    variant: 'primary',
                    onClick: () => props.confirm_enabled && emit('confirm'),
                }, () => props.confirm_label),
            ],
        });
    },
});
